import { createClient, type Interceptor } from "@connectrpc/connect";
import { createConnectTransport } from "@connectrpc/connect-web";
import {
  BaseService,
  MetadataService,
  EntityService,
  AuthService,
  EventBusService,
  SectionService,
  ThemeService,
  RolePermissionService,
  DBaseTypeService,
  SyncService,
  LogService,
  ConfigService,
  IndicatorService,
  DBaseOperationService,
  ConnectorService,
  MetricService,
  EventPageService,
  EquipmentService,
  IoTService,
  AnalyticsService,
  FormPropertiesService,
  LockService,
  ReportService,
  FileService,
  EventLogService,
  BackupService,
  PackageService,
  StatusService,
  TaskService,
  OrderService,
  ExternalApiService,
  PushService,
  LocationService,
  MessengerService,
  StatementService,
  ModuleService,
  AiService,
  PageService,
  PollService,
  PictureService,
  ClassifierService,
  GarService,
  MetricsService,
  FolderService,
  InstanceService,
  MediaService,
  AdapterService,
  JobsService,
  SiteService,
  SiteTemplateService,
  ReviewService,
  AppService,
  AccountingService,
  BillingService,
  PaymentService,
  SchedulerService,
  VerifyService,
  CalendarService,
  ApprovalService,
  SignatureService,
  VpnService,
  TagService,
  RemoteService,
  AssetService,
  HydraIdService,
  DeviceService,
  MobileService,
  GeoService,
  CallService,
  IdentityService,
  ReferenceService,
  FedChatService,
  VoiceRoomService,
  SupportService,
  InviteService,
  EcgService,
  MedicService,
  DevopsService,
  MailService,
} from "./gen/zdc_pb";

export interface ZDCSDKConfig {
  baseUrl: string;
  token?: string;
  /** Активная база (slug): заголовок X-Hydra-Base на ВСЕ запросы. */
  base?: string;
  /**
   * Резолвер активной базы — вызывается в МОМЕНТ КАЖДОГО запроса (приоритетнее
   * статического `base`). Устраняет гонку гидратации стора: SDK мог быть создан
   * до восстановления baseSlug из localStorage, и запросы уходили без
   * X-Hydra-Base (400 «no base selected»). Клиент передаёт
   * `() => useCenterStore.getState().baseSlug || ''`.
   */
  baseResolver?: () => string;
  /** Таймаут запроса в мс (по умолчанию 20000). */
  requestTimeoutMs?: number;
  /**
   * Колбэк на ошибку ЛЮБОГО RPC (кроме стримов). Вызывается с методом и полной
   * ошибкой (code/rawMessage/cause/metadata). Клиент пишет его в журнал
   * регистрации (категория ui) — вместо того чтобы искать ошибки по экранам.
   */
  onRpcError?: (method: string, error: unknown) => void;
}

export class ZDCSDK {
  private token: string | null;
  private mode: string | null;
  private baseSlug: string | null = null;
  private baseResolver: (() => string) | null = null;
  private desktopClient = false;
  private readonly requestTimeoutMs: number;
  private readonly onRpcError?: (method: string, error: unknown) => void;

  public base: ReturnType<typeof createClient<typeof BaseService>>;
  public metadata: ReturnType<typeof createClient<typeof MetadataService>>;
  public entity: ReturnType<typeof createClient<typeof EntityService>>;
  public auth: ReturnType<typeof createClient<typeof AuthService>>;
  public eventBus: ReturnType<typeof createClient<typeof EventBusService>>;
  public section: ReturnType<typeof createClient<typeof SectionService>>;
  public theme: ReturnType<typeof createClient<typeof ThemeService>>;
  public rolePerm: ReturnType<typeof createClient<typeof RolePermissionService>>;
  public dbaseType: ReturnType<typeof createClient<typeof DBaseTypeService>>;
  public sync: ReturnType<typeof createClient<typeof SyncService>>;
  public log: ReturnType<typeof createClient<typeof LogService>>;
  public config: ReturnType<typeof createClient<typeof ConfigService>>;
  public indicator: ReturnType<typeof createClient<typeof IndicatorService>>;
  public dbaseOp: ReturnType<typeof createClient<typeof DBaseOperationService>>;
  public connector: ReturnType<typeof createClient<typeof ConnectorService>>;
  public metric: ReturnType<typeof createClient<typeof MetricService>>;
  public eventPage: ReturnType<typeof createClient<typeof EventPageService>>;
  public equipment: ReturnType<typeof createClient<typeof EquipmentService>>;
  public iot: ReturnType<typeof createClient<typeof IoTService>>;
  public analytics: ReturnType<typeof createClient<typeof AnalyticsService>>;
  public formProperties: ReturnType<typeof createClient<typeof FormPropertiesService>>;
  public lock: ReturnType<typeof createClient<typeof LockService>>;
  public report: ReturnType<typeof createClient<typeof ReportService>>;
  public files: ReturnType<typeof createClient<typeof FileService>>;
  public eventLog: ReturnType<typeof createClient<typeof EventLogService>>;
  public backup: ReturnType<typeof createClient<typeof BackupService>>;
  public package: ReturnType<typeof createClient<typeof PackageService>>;
  public status: ReturnType<typeof createClient<typeof StatusService>>;
  public task: ReturnType<typeof createClient<typeof TaskService>>;
  public order: ReturnType<typeof createClient<typeof OrderService>>;
  public externalApi: ReturnType<typeof createClient<typeof ExternalApiService>>;
  public push: ReturnType<typeof createClient<typeof PushService>>;
  public location: ReturnType<typeof createClient<typeof LocationService>>;
  /** Внутренний чат: чаты/сообщения/вложения/обои + ссылки-приглашения
   *  (createConversationInvite; resolveConversationInvite — публичный
   *  предпросмотр; redeemConversationInvite — погашение, гость без входа). */
  public messenger: ReturnType<typeof createClient<typeof MessengerService>>;
  public statement: ReturnType<typeof createClient<typeof StatementService>>;
  public module: ReturnType<typeof createClient<typeof ModuleService>>;
  public ai: ReturnType<typeof createClient<typeof AiService>>;
  public page: ReturnType<typeof createClient<typeof PageService>>;
  public site: ReturnType<typeof createClient<typeof SiteService>>;
  /** ШАБЛОНЫ САЙТОВ (библиотека уровня установки): галерея/карточка/ассеты/
   *  применение к сайту + обслуживание (Reload/Check/Install из Hub). */
  public siteTemplates: ReturnType<typeof createClient<typeof SiteTemplateService>>;
  public review: ReturnType<typeof createClient<typeof ReviewService>>;
  public poll: ReturnType<typeof createClient<typeof PollService>>;
  public picture: ReturnType<typeof createClient<typeof PictureService>>;
  public classifier: ReturnType<typeof createClient<typeof ClassifierService>>;
  public gar: ReturnType<typeof createClient<typeof GarService>>;
  public metrics: ReturnType<typeof createClient<typeof MetricsService>>;
  public folders: ReturnType<typeof createClient<typeof FolderService>>;
  public instance: ReturnType<typeof createClient<typeof InstanceService>>;
  public media: ReturnType<typeof createClient<typeof MediaService>>;
  public adapter: ReturnType<typeof createClient<typeof AdapterService>>;
  public jobs: ReturnType<typeof createClient<typeof JobsService>>;
  /** Сервис-приложения (под-этап A): конфиг/start/stop/логи/секреты/токен. */
  public appService: ReturnType<typeof createClient<typeof AppService>>;
  /** Учёт (шаг 1): планы счетов/счета/виды субконто (конфигуратор). */
  public accounting: ReturnType<typeof createClient<typeof AccountingService>>;
  /** БИЛЛИНГ (под-этап A): настройки/счета/операции/доступы/тарифы/подписки. */
  public billing: ReturnType<typeof createClient<typeof BillingService>>;
  /** ПЛАТЕЖИ: универсальный приём денег (провайдеры: mock/manual/yookassa;
   *  создание платежа, история, возврат) — для сайтов/приложений/игр/баз. */
  public payments: ReturnType<typeof createClient<typeof PaymentService>>;
  /** ПЛАНИРОВЩИК: регламентные задания (расписание/обработчик/история запусков). */
  public scheduler: ReturnType<typeof createClient<typeof SchedulerService>>;
  /** M1 «Подтверждения и доставка»: коды/решения по каналам (email/telegram/max/webpush/in-app). */
  public verify: ReturnType<typeof createClient<typeof VerifyService>>;
  /** M5 «Календарь»: события + агрегация сроков задач в диапазоне дат. */
  public calendar: ReturnType<typeof createClient<typeof CalendarService>>;
  public tags: ReturnType<typeof createClient<typeof TagService>>;
  /** Удалённый доступ (Ф4): гостевые ссылки на сеанс (создание/резолв токена). */
  public remote: ReturnType<typeof createClient<typeof RemoteService>>;
  /** СЭД: маршруты согласования (шаблоны/экземпляры/шаги, решения approve/reject). */
  public approval: ReturnType<typeof createClient<typeof ApprovalService>>;
  /** ЭЦП: реестр подписей (.sig к версии файла, статусы проверки). */
  public signature: ReturnType<typeof createClient<typeof SignatureService>>;
  /** VPN: профили подключений (секреты шифрованы; конфиг wireguard). */
  public vpn: ReturnType<typeof createClient<typeof VpnService>>;
  /** АССЕТЫ С ОБЛАСТЯМИ (core|site|app|game|package): List/Save/Delete (админ)
   *  + публичный ResolveAsset(site, code) — свой ассет → общий core. */
  public assets: ReturnType<typeof createClient<typeof AssetService>>;
  /** HydraID (локальный MVP): глобальный номер человека `AAA BBB CCC D`
   *  (Verhoeff). GetMyHydraId (+QR `hydra:<n>`), ResolveNumber, SearchByNumber. */
  public hydraId: ReturnType<typeof createClient<typeof HydraIdService>>;
  /** УСТРОЙСТВА HydraID: DeviceID = `{HydraID}:{Index}` — реестр устройств
   *  человека (ListMyDevices/RegisterDevice/TouchDevice/RevokeDevice). */
  public device: ReturnType<typeof createClient<typeof DeviceService>>;
  /** МОБИЛЬНОЕ ПРИЛОЖЕНИЕ (Фаза 0, контракт sdk.mobile.v1): фасад MobileService —
   *  device-bound. RegisterDevice/GetCapabilities/GetVpnConfig/RotateVpnConfig/
   *  LookupNumber/SyncBlocklist/PushPolicy/ReportCallEvent. */
  public mobile: ReturnType<typeof createClient<typeof MobileService>>;
  /** ЕДИНЫЙ ГЕО-ШОВ: подсказки адресов/поиск организации/геокодинг/обратный/
   *  resolve (кэш справочников → роутер провайдеров; ответы несут source/cached). */
  public geo: ReturnType<typeof createClient<typeof GeoService>>;
  /** ЗВОНКИ (механизм ядра): состояние звонка + доставка входящего, в т.ч.
   *  Web Push в ЗАКРЫТОЕ приложение (клик открывает приложение, клиент
   *  подхватывает звонок через listActiveCalls). startCall (комната conv_<id> —
   *  адресаты = участники чата), answerCall/declineCall/endCall (идемпотентно,
   *  только участники), getCall/listActiveCalls. */
  public calls: ReturnType<typeof createClient<typeof CallService>>;
  /** ФЕДЕРАЦИЯ, Фаза 1 «Основа доверия» — ИДЕНТИЧНОСТЬ базы: DID-документ
   *  (`did:hydra:<uuid>`), ключи root→signing, ротация ключа, реестр корней
   *  доверия (trust roots), экспорт корневого сертификата. */
  public identity: ReturnType<typeof createClient<typeof IdentityService>>;
  /** ФЕДЕРАЦИЯ — ПОДПИСАННЫЕ СПРАВОЧНИКИ (артефакты): публикация снапшота/
   *  дельты (админ/роль «Федерация — управление»), каталог публикаций, файл
   *  `.hydra-ref`, offline-импорт с проверкой подписи/цепочки/seq, явный
   *  откат, подписки, статус. */
  public reference: ReturnType<typeof createClient<typeof ReferenceService>>;
  /** МЕЖБАЗОВЫЙ ЧАТ ЧЕРЕЗ ХАБ (волна 1b): личные сообщения между
   *  пользователями РАЗНЫХ баз (store-and-forward через центр), доставка —
   *  в ЕДИНЫЙ direct-чат платформы (обычный messenger.sendMessage).
   *  `startChatByHydraId({hydraId})` — открыть чат по номеру сети.
   *  Межбазовый чат выключен настройкой базы → FailedPrecondition. */
  public fedChat: ReturnType<typeof createClient<typeof FedChatService>>;
  /** ГОЛОСОВЫЕ КОМНАТЫ (волна 2, D7): постоянные комнаты как в Discord —
   *  вошёл-вышел, видно «кто внутри». Состояние/присутствие/роли ведёт сервер
   *  (voice_rooms/voice_room_members), медиа — LiveKit: `getRoomToken` выдаёт
   *  пропуск участнику в комнате (комната voice_<roomId>). Управление —
   *  создатель/модератор/админ; события шины
   *  voice.room.joined|left|updated|deleted.{roomId}. */
  public voiceRooms: ReturnType<typeof createClient<typeof VoiceRoomService>>;
  /** ИНБОКС ПОДДЕРЖКИ (§3.4): обращения к «Поддержке» как очередь специалистов
   *  (listTickets/getTicket/assignTicket/setTicketStatus/setTicketPriority/
   *  replyTicket + шаблоны ответов). Роль «Поддержка»/админ — весь инбокс,
   *  пользователь — только свои обращения. События support.ticket.*. */
  public support: ReturnType<typeof createClient<typeof SupportService>>;
  /** ПРИГЛАШЕНИЯ И ДЕРЕВО УЧАСТНИКОВ (ядро): createInvite (любой
   *  пользователь), resolveInvite (публичный предпросмотр кода),
   *  acceptInvite (регистрация/вход, идемпотентно), listMyInvited (дерево;
   *  чужие — только админ), listInviteRewards (доходы: роль
   *  «Приглашения — программа» или админ). Вознаграждения НЕ начисляются
   *  автоматически — только настройки базы settings.invites. */
  public invites: ReturnType<typeof createClient<typeof InviteService>>;
  /** ЭКГ: серверный разбор снимка + доступность анализатора. */
  public ecg: ReturnType<typeof createClient<typeof EcgService>>;
  /** КАРТА ПАЦИЕНТА (ГидраМедик): серверные команды — назначение/передача
   *  лечащего врача (история команды в одной транзакции) и импорт анализов
   *  CSV (построчные ошибки, исходный файл сохраняется в «Файлах»). */
  public medic: ReturnType<typeof createClient<typeof MedicService>>;
  /** ПАНЕЛЬ РАЗРАБОТКИ: узлы, снимки, задачи и разрешённые действия. */
  public devops: ReturnType<typeof createClient<typeof DevopsService>>;
  /** ПОЧТА: управление ящиками (list/get/save/delete/reconnect/test). Доступ —
   *  администратор или полномочие mail_access; не-админ — только свои ящики.
   *  Секреты (пароли/токены) НЕ возвращаются: только *_set и срок токена. */
  public mail: ReturnType<typeof createClient<typeof MailService>>;

  constructor(config: ZDCSDKConfig) {
    this.token = config.token || null;
    this.mode = null;
    this.baseSlug = config.base || null;
    this.baseResolver = config.baseResolver || null;
    this.requestTimeoutMs = config.requestTimeoutMs || 20000;
    this.onRpcError = config.onRpcError;

    // Единый транспорт: токен + режим интерфейса + ТАЙМАУТ + ЛОГИРОВАНИЕ.
    // Таймаут обязателен: без него запрос к зависшему серверу висит вечно
    // («Загрузка...» в лаунчере). Ошибки логируются в консоль (диагностика
    // через CDP/агент); клиент дополнительно пишет их в журнал регистрации.
    const transport = createConnectTransport({
      baseUrl: config.baseUrl,
      interceptors: [
        (next) => async (req) => {
          if (this.token) {
            req.header.set("Authorization", `Bearer ${this.token}`);
          }
          // Режим интерфейса (как в 1С: Конфигуратор видит ВСЕ объекты,
          // Предприятие — по функциональным опциям): сервер применяет
          // функциональные опции ТОЛЬКО для enterprise-запросов.
          if (this.mode) {
            req.header.set("X-Hydra-Mode", this.mode);
          }
          // Активная база (базы-как-схемы): сервер требует X-Hydra-Base для
          // не-discovery запросов (без default-базы). baseResolver (если задан)
          // читает базу В МОМЕНТ запроса — без гонки гидратации стора.
          const activeBase = this.baseResolver ? this.baseResolver() : this.baseSlug;
          if (activeBase) {
            req.header.set("X-Hydra-Base", activeBase);
          }
          if (this.desktopClient) {
            req.header.set("X-Hydra-Client", "desktop");
          }
          const method = String(req.method || "?");
          const started = Date.now();
          // ДИАГНОСТИКА (базы-как-схемы): каждый запрос логирует базу —
          // видно, уходит ли X-Hydra-Base и куда.
          console.log(`[zdc-sdk] ${method} base="${this.baseSlug || ''}"`);
          try {
            const resp = await next(req);
            const ms = Date.now() - started;
            if (ms > 5000) {
              // Медленный запрос — предупреждение (диагностика узких мест)
              console.warn(`[zdc-sdk] ${method} ok (${ms}ms)`);
            }
            return resp;
          } catch (e: unknown) {
            const ms = Date.now() - started;
            // Полный разбор ошибки для диагностики (код/rawMessage/стек):
            // connect-web кидает ConnectError — message может быть невнятным
            // («missing trailer»), а причина — в rawMessage/code/metadata.
            const detail = e instanceof Error ? e.message : String(e);
            let extra = '';
            try {
              const anyE = e as Record<string, unknown>;
              if (typeof anyE['code'] === 'string') extra += ` code=${String(anyE['code'])}`;
              if (typeof anyE['rawMessage'] === 'string') extra += ` raw=${String(anyE['rawMessage'])}`;
              if (typeof anyE['cause'] === 'string' && String(anyE['cause']) !== '') extra += ` cause=${String(anyE['cause'])}`;
              const md = anyE['metadata'] as { get?: (k: string) => string | undefined } | undefined;
              const gm = md?.get?.('grpc-message');
              if (gm) extra += ` grpc-message=${gm}`;
              if (e instanceof Error && e.stack) extra += ` stack=${e.stack.split('\n').slice(0, 3).join(' | ')}`;
            } catch {}
            console.error(`[zdc-sdk] ${method} FAILED (${ms}ms): ${detail}${extra}`);
            // Журнал регистрации (клиент регистрирует колбэк — см. sdkStore).
            if (this.onRpcError && req.stream !== true) {
              try { this.onRpcError(method, e); } catch {}
            }
            throw e;
          }
        },
        // Таймаут: 20 секунд на ОДИНОЧНЫЙ запрос (ConnectRPC/grpc-web).
        // Зависший сервер → явная ошибка, а не бесконечное ожидание.
        // СТРИМЫ (EventBus/Subscribe — долгоживущие) таймаутом НЕ обрываются:
        // req.stream === true (иначе событийный канал рвётся каждые 20с).
        // ЯВНЫЙ дедлайн вызова (CallOptions.timeoutMs → заголовок
        // Connect-Timeout-Ms устанавливается connect ДО интерцепторов) —
        // уважаем как есть: connect сам следит за дедлайном (req.signal) и
        // сервер получает то же значение. Дефолт 20с НЕ навязываем — иначе
        // долгие операции (создание базы ~минута) обрывались на 20-й секунде
        // («тишина» в интерфейсе, сервер продолжал работу вхолостую).
        (next) => async (req) => {
          if (req.stream === true) return next(req);
          if (req.header.get("Connect-Timeout-Ms")) return next(req);
          const controller = new AbortController();
          const timer = setTimeout(() => controller.abort(), this.requestTimeoutMs);
          req.signal.addEventListener("abort", () => controller.abort(), { once: true });
          try {
            return await next({ ...req, signal: controller.signal });
          } finally {
            clearTimeout(timer);
          }
        },
      ],
    });

    this.base = createClient(BaseService, transport);
    this.metadata = createClient(MetadataService, transport);
    this.entity = createClient(EntityService, transport);
    this.auth = createClient(AuthService, transport);
    this.eventBus = createClient(EventBusService, transport);
    this.section = createClient(SectionService, transport);
    this.theme = createClient(ThemeService, transport);
    this.rolePerm = createClient(RolePermissionService, transport);
    this.dbaseType = createClient(DBaseTypeService, transport);
    this.sync = createClient(SyncService, transport);
    this.log = createClient(LogService, transport);
    this.config = createClient(ConfigService, transport);
    this.indicator = createClient(IndicatorService, transport);
    this.dbaseOp = createClient(DBaseOperationService, transport);
    this.connector = createClient(ConnectorService, transport);
    this.metric = createClient(MetricService, transport);
    this.eventPage = createClient(EventPageService, transport);
    this.equipment = createClient(EquipmentService, transport);
    this.iot = createClient(IoTService, transport);
    this.analytics = createClient(AnalyticsService, transport);
    this.formProperties = createClient(FormPropertiesService, transport);
    this.lock = createClient(LockService, transport);
    this.report = createClient(ReportService, transport);
    this.files = createClient(FileService, transport);
    this.eventLog = createClient(EventLogService, transport);
    this.backup = createClient(BackupService, transport);
    this.package = createClient(PackageService, transport);
    this.status = createClient(StatusService, transport);
    this.task = createClient(TaskService, transport);
    this.order = createClient(OrderService, transport);
    this.externalApi = createClient(ExternalApiService, transport);
    this.push = createClient(PushService, transport);
    this.location = createClient(LocationService, transport);
    this.messenger = createClient(MessengerService, transport);
    this.statement = createClient(StatementService, transport);
    this.module = createClient(ModuleService, transport);
    this.ai = createClient(AiService, transport);
    this.page = createClient(PageService, transport);
    this.site = createClient(SiteService, transport);
    this.siteTemplates = createClient(SiteTemplateService, transport);
    this.review = createClient(ReviewService, transport);
    this.adapter = createClient(AdapterService, transport);
    this.jobs = createClient(JobsService, transport);
    this.appService = createClient(AppService, transport);
    this.accounting = createClient(AccountingService, transport);
    this.billing = createClient(BillingService, transport);
    this.payments = createClient(PaymentService, transport);
    this.scheduler = createClient(SchedulerService, transport);
    this.verify = createClient(VerifyService, transport);
    this.calendar = createClient(CalendarService, transport);
    this.tags = createClient(TagService, transport);
    this.remote = createClient(RemoteService, transport);
    this.approval = createClient(ApprovalService, transport);
    this.signature = createClient(SignatureService, transport);
    this.vpn = createClient(VpnService, transport);
    this.assets = createClient(AssetService, transport);
    this.hydraId = createClient(HydraIdService, transport);
    this.device = createClient(DeviceService, transport);
  this.mobile = createClient(MobileService, transport);
    this.geo = createClient(GeoService, transport);
    this.poll = createClient(PollService, transport);
    this.picture = createClient(PictureService, transport);
    this.classifier = createClient(ClassifierService, transport);
    this.gar = createClient(GarService, transport);
    this.metrics = createClient(MetricsService, transport);
    this.folders = createClient(FolderService, transport);
    this.instance = createClient(InstanceService, transport);
    this.media = createClient(MediaService, transport);
    this.calls = createClient(CallService, transport);
    this.identity = createClient(IdentityService, transport);
    this.reference = createClient(ReferenceService, transport);
    this.fedChat = createClient(FedChatService, transport);
    this.voiceRooms = createClient(VoiceRoomService, transport);
    this.support = createClient(SupportService, transport);
    this.invites = createClient(InviteService, transport);
    this.ecg = createClient(EcgService, transport);
    this.medic = createClient(MedicService, transport);
    this.devops = createClient(DevopsService, transport);
    this.mail = createClient(MailService, transport);
  }

  setToken(token: string | null) {
    this.token = token;
  }

  /** Режим интерфейса: 'configurator' | 'enterprise' (для функциональных опций). */
  setMode(mode: string | null) {
    this.mode = mode;
  }

  /** Активная база (slug) — заголовок X-Hydra-Base на все запросы. */
  setBase(slug: string | null) {
    this.baseSlug = slug;
  }

  /** Резолвер активной базы на КАЖДЫЙ запрос (приоритетнее setBase). */
  setBaseResolver(fn: (() => string) | null) {
    this.baseResolver = fn;
  }

  /** Desktop-клиент (Wails): заголовок X-Hydra-Client: desktop на все запросы.
   *  Сервер использует его вместо UA для определения типа клиента. */
  setDesktopClient(desktop: boolean) {
    this.desktopClient = desktop;
  }
}

export function createSDK(config: ZDCSDKConfig): ZDCSDK {
  return new ZDCSDK(config);
}

// Re-export generated types from zdc_pb (v2 format)
export * from "./gen/zdc_pb";

